import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from "react-i18next";

const FormSelect = ({ children, options, handleSelect, value, placeholder, optionListHeight, disabled, ...props }) => {
  const { t } = useTranslation();
  const optionListMaxHeight = optionListHeight || 200;
  const [isOpen, setIsOpen] = useState(false);
  const [optionListLocation, setOptionListLocation] = useState("bottom");
  const selectContainerRef = useRef(null);
  const nodeRef = React.useRef(null)

  const handleBlur = () => {
    setIsOpen(false);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (selectContainerRef.current) {
      const rect = selectContainerRef.current.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const optionListHeight = nodeRef?.current?.offsetHeight ?? 0;
      setOptionListLocation(document.documentElement.scrollHeight - absoluteTop - 50 > optionListHeight ? "bottom" : "top");
    }
  }, [isOpen]);

  return (
    <SelectContainer ref={selectContainerRef} $isOpen={isOpen} {...props}>
      <Select $color={value?.color} $bold={value?.bold} $disabled={disabled} onClick={!disabled ? handleClick : null} onBlur={handleBlur}>{value?.value ? options.find(option => option.value === value.value)?.label : placeholder ? placeholder : t("pleaseSelect", { key: "" })}</Select>

      <CSSTransition
        nodeRef={nodeRef}
        in={isOpen}
        timeout={250}
        classNames="fade"
        unmountOnExit
      >
        <OptionList ref={nodeRef} $optionListMaxHeight={optionListMaxHeight} $optionListLocation={optionListLocation}>
          {
            options.map((option) => (
              <div key={option.value}>
                {
                  value?.value === option.value ?
                    <OptionActive $color={option.color} title={option.label} $bold={option.bold} onClick={handleSelect ? () => { handleSelect(option); } : null}>
                      <>{option.label}</>
                      <img src={`${process.env.PUBLIC_URL}/images/tick.png`} alt="selected" />
                    </OptionActive>
                    :
                    <Option $color={option.color} title={option.label} $bold={option.bold} $disabled={option.disabled} onClick={!option.disabled && handleSelect ? () => { handleSelect(option); } : null}>{option.label}</Option>
                }
              </div>
            ))
          }
        </OptionList>
      </CSSTransition>
    </SelectContainer>
  );
};

const SelectContainer = styled.div`
  position: relative;
  &:before {
    position: absolute;
    right: 1rem;
    pointer-events: none;
    content: url(${process.env.PUBLIC_URL}/images/chevron-d.svg);
    height: 24px;
    width: 24px;
    top: 12px;
    transition: transform 0.2s ease-in-out;
    transform: ${props => props.$isOpen ? 'rotate(-180deg)' : 'rotate(0deg)'};
  }
`;

const Select = styled.button`
  background-color: var(--aia-bx-white);
  ${props => props.$disabled && `background-color: var(--aia-bx-gray-4);`};
  border: 1px solid var(--aia-bx-gray-5);
  border-radius: 100px;
  text-align: left;
  font-size: 16px;
  line-height: 16px;
  padding: 15px 41px 15px 16px;
  width: 100%;
  cursor: pointer;
  outline-color: var(--aia-bx-blue-2);
  text-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${props => props.$color && `color: ${props.$color};`};
  ${props => props.$bold && `font-weight: bold;`};
  &:hover, &:focus {
    border-color: var(--aia-bx-blue-2);
    box-shadow: 0px 0px 0.5rem 0px rgba(2, 114, 174, 0.2);
  }
`;

const OptionList = styled.ul`
  width: 100%;
  max-height: ${props => props.$optionListMaxHeight}px;
  overflow-y: auto;
  position: absolute;
  bottom: ${props => props.$optionListLocation === "top" ? "55px" : "auto"};
  display: flex;
  flex-direction: column;
  margin: 5px 0px 0px;
  cursor: pointer;
  padding: 10px;
  box-shadow: 0px 0px 0.5rem 0px rgba(2, 114, 174, 0.2);
  border-radius: 0.5rem;
  background-color: var(--aia-bx-white);
  z-index: 100;
`;

const Option = styled.li`
  padding: 8px 10px;
  list-style-type: none;
  border-radius: 0.5rem;
  font-size: 14px;
  ${props => props.$color && `color: ${props.$color};`};
  ${props => props.$disabled && `color: var(--aia-bx-gray-5);`};
  ${props => props.$disabled && `cursor: not-allowed;`};
  ${props => props.$bold && `font-weight: bold;`};

  &:hover {
    background-color: var(--aia-bx-gray-10);
  }
`;

const OptionActive = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  list-style-type: none;
  border-radius: 5px;
  font-size: 14px;
  ${props => props.$color ? `color: ${props.$color};` : "color: var(--aia-bx-dark-blue);"};
  ${props => props.$bold && `font-weight: bold;`};

  &:hover {
    background-color: var(--aia-bx-gray-10);
  }
`;

export default FormSelect;